"use client"

import React, { useEffect } from "react"
import NextLink from "next/link"
import { BreadcrumbResponse } from "@/app/(site)/_templates/helper/getBreadcrumb"
import { Slot } from "@radix-ui/react-slot"
import TC_Wrapper from "react-tag-commander"

import { buildAnalyticsLinks, cn } from "@/lib/utils"
import { RemixIcon } from "@/components/ui/atoms"

interface IBreadcrumbProps {
  asChild?: boolean
  pageType?: string
  breadcrumb: BreadcrumbResponse
}

const Breadcrumb = React.forwardRef<HTMLDivElement, IBreadcrumbProps>(
  ({ asChild = false, breadcrumb, pageType, ...props }, ref) => {
    const Component = asChild ? Slot : "div"
    const wrapper = TC_Wrapper.getInstance()

    // Function to adjust breadcrumb's appearance based on width
    function adjustBreadcrumb() {
      const breadcrumbWraps = document.querySelectorAll(".breadcrumb-wrap")

      breadcrumbWraps.forEach((wrap, index) => {
        if (index === 0) {
          return
        }

        const wStyle = window.getComputedStyle(wrap)
        let wWidth =
          wrap.clientWidth -
          parseFloat(wStyle.paddingLeft) -
          parseFloat(wStyle.paddingRight)

        wrap.classList.toggle("breadcrumb-wrap--sm", wWidth <= 10)
      })
    }

    function handleBreadcrumbClick(
      e: React.MouseEvent<HTMLAnchorElement>,
      link: string
    ) {
      const analytics = buildAnalyticsLinks(
        [link],
        e.currentTarget,
        "navigation"
      )
      wrapper.triggerEvent(analytics.type, e.currentTarget, analytics.chapters)
    }

    useEffect(() => {
      adjustBreadcrumb() // Adjust breadcrumb on mount

      const handleResize = () => adjustBreadcrumb()
      window.addEventListener("resize", handleResize)

      return () => window.removeEventListener("resize", handleResize)
    }, [])

    return (
      <Component className="breadcrumb-main container" ref={ref} {...props}>
        <div className="breadcrumb-container">
          <nav className="breadcrumb-nav" aria-label="breadcrumb">
            {breadcrumb.breadcrumbLinks
              ? breadcrumb.breadcrumbLinks.map((link, i) => {
                  const isCurrent = link.current || false
                  return (
                    <div
                      className="breadcrumb-wrap"
                      key={link.url}
                      id={`breadcrumb-${i}`}
                    >
                      <span className="breadcrumb-icon">
                        <RemixIcon name="ArrowRightS" />
                      </span>
                      <span className="breadcrumb">
                        <NextLink
                          href={link.url}
                          onClick={(e) => handleBreadcrumbClick(e, link.title)}
                          className={cn({ "pointer-events-none": isCurrent })}
                          aria-current={isCurrent ? "page" : undefined}
                          aria-disabled={isCurrent ? true : undefined}
                        >
                          {link.title}
                        </NextLink>
                      </span>
                    </div>
                  )
                })
              : undefined}
          </nav>
        </div>
      </Component>
    )
  }
)

export { Breadcrumb, type IBreadcrumbProps }
